import {
  BACKEND_VITE_CACHE_DIR,
  getAppBackendBuildFolder,
} from '@wix/cli-app-definitions';
import type { InlineConfig } from 'vite';
import tsconfigPaths from '@wix/vite-tsconfig-paths';
import { viteMiniflarePlugin } from '@wix/cli-miniflare';
import type { AppSecrets } from '@wix/dev-center-client';
import { createRequire } from 'node:module';
import { optimizeBackendExtensions } from '@wix/vite-optimize-backend-dependencies';
import type { ProjectModel } from '../../model.js';
import type { Logger } from '../logger.js';
import {
  EVENTS_PATTERN,
  SERVICE_PLUGINS_PATTERN,
} from '../../dev-center/components/backend/components.js';

const require = createRequire(import.meta.url);

export function getViteConfig(
  model: ProjectModel,
  options: { customLogger: Logger },
  { appSecret, webhookPublicKey }: AppSecrets
): InlineConfig {
  const entry = require.resolve(
    '@wix/cli-app/internal/cloudflare-runtime/entry'
  );

  const getRegisteredExtensions = require.resolve(
    '@wix/cli-app/internal/cloudflare-runtime/getRegisteredExtensions'
  );

  return {
    configFile: false,
    root: model.projectFolder,
    customLogger: options.customLogger,
    clearScreen: false,
    logLevel: 'info',
    cacheDir: BACKEND_VITE_CACHE_DIR,
    optimizeDeps: {
      entries: [entry, getRegisteredExtensions],
    },
    server: {
      cors: { origin: true },
      allowedHosts: ['.wix-tunnel.run'],
    },
    environments: {
      ssr: {
        resolve: {
          noExternal: true,
          conditions: ['workerd'],
        },
      },
    },
    resolve: {
      alias: {
        // polyfill `querystring` until it's supported by cloudflare
        // https://developers.cloudflare.com/workers/runtime-apis/nodejs
        querystring: 'query-string',
      },
    },
    build: {
      ssr: true,
      manifest: true,
      sourcemap: true,
      target: 'esnext',
      outDir: getAppBackendBuildFolder(model.projectFolder),
      rollupOptions: {
        output: {
          entryFileNames: `[name].mjs`,
          chunkFileNames: `assets/[name].mjs`,
        },
        input: {
          entry,
          getRegisteredExtensions,
        },
      },
    },
    define: {
      'import.meta.env.APP_ID': JSON.stringify(model.config.appId),
      'import.meta.env.APP_SECRET': JSON.stringify(appSecret),
      'import.meta.env.PUBLIC_KEY': JSON.stringify(webhookPublicKey),
      'import.meta.env.SENTRY_DSN': JSON.stringify(
        model.config.monitoring && 'sentry' in model.config.monitoring
          ? model.config.monitoring.sentry.dsn
          : undefined
      ),
    },
    plugins: [
      optimizeBackendExtensions({
        projectFolder: model.projectFolder,
        exclude: [entry],
      }),
      viteMiniflarePlugin({
        entry,
        workerEntry: require.resolve(
          '@wix/cli-app/internal/miniflare/worker-entry'
        ),
        customLogger: options.customLogger,
        invalidateGlobPatterns: [EVENTS_PATTERN, SERVICE_PLUGINS_PATTERN],
      }),
      tsconfigPaths({
        ignoreConfigErrors: true,
        root: model.projectFolder,
      }),
    ],
  };
}
